import { TELANGANA_DISTRICTS, telanganaDistrictKeyFromStored } from "../data/telanganaDistricts";
import type { GeoDirectoryStore } from "../data/geoDirectoryStore";
import { storedDistrictMatchesDirectoryKey } from "../data/geoDirectoryStore";
import { normalizeGeoLabel } from "./geoLabel";
import { toDisplayCamelCase } from "./textCase";
import type { Application, ApplicationStatus } from "../types";

export const REPORT_STATUS_ORDER: ApplicationStatus[] = [
  "pending_validation",
  "on_hold",
  "ready_for_icici",
  "ready_for_mou",
  "icici_recorded",
  "subcontractor_linked",
];

export const REPORT_STATUS_LABELS: Record<ApplicationStatus, string> = {
  pending_validation: "Pending validation",
  on_hold: "On hold",
  ready_for_icici: "Ready for ICICI",
  ready_for_mou: "Ready for MOU",
  icici_recorded: "Awaiting subcontractor",
  subcontractor_linked: "Subcontractor linked",
};

export type GeoReportRow = {
  key: string;
  label: string;
  total: number;
  byStatus: Record<ApplicationStatus, number>;
};

function emptyByStatus(): Record<ApplicationStatus, number> {
  return {
    pending_validation: 0,
    on_hold: 0,
    ready_for_icici: 0,
    ready_for_mou: 0,
    icici_recorded: 0,
    subcontractor_linked: 0,
  };
}

function countStatuses(apps: Application[]): Record<ApplicationStatus, number> {
  const out = emptyByStatus();
  for (const a of apps) {
    out[a.status] += 1;
  }
  return out;
}

/** One row per district from geo directory (zeros where no applicants). */
export function districtReportRows(apps: Application[], geo?: GeoDirectoryStore | null): GeoReportRow[] {
  if (geo) {
    return geo.tgRegistrationDistricts().map((d) => {
      const sub = apps.filter((a) => storedDistrictMatchesDirectoryKey(geo, d.districtKey, a.district));
      return {
        key: d.districtKey,
        label: toDisplayCamelCase(d.registrationDistrictName),
        total: sub.length,
        byStatus: countStatuses(sub),
      };
    });
  }
  return TELANGANA_DISTRICTS.map((d) => {
    const sub = apps.filter((a) => telanganaDistrictKeyFromStored(a.district) === d.key);
    return {
      key: d.key,
      label: d.label,
      total: sub.length,
      byStatus: countStatuses(sub),
    };
  });
}

/**
 * Mandals for the district: full geo directory (zeros where no applicants), plus any
 * applicant mandal names not present in the directory.
 */
export function mandalReportRows(
  apps: Application[],
  district: string,
  geo: GeoDirectoryStore,
): GeoReportRow[] {
  const sub = apps.filter((a) => storedDistrictMatchesDirectoryKey(geo, district, a.district));
  const appMap = new Map<string, Application[]>();
  for (const a of sub) {
    const raw = (a.mandal || "").trim();
    const nk = raw ? normalizeGeoLabel(raw) : "";
    if (!appMap.has(nk)) appMap.set(nk, []);
    appMap.get(nk)!.push(a);
  }

  const dir = geo.tgMandalsForDistrict(district);
  const rows: GeoReportRow[] = [];
  const usedNorm = new Set<string>();

  for (const m of dir) {
    const nk = normalizeGeoLabel(m.mandalName);
    const list = appMap.get(nk) ?? [];
    usedNorm.add(nk);
    rows.push({
      key: m.mandalName,
      label: m.mandalName,
      total: list.length,
      byStatus: countStatuses(list),
    });
  }

  for (const [nk, list] of appMap) {
    if (!nk || usedNorm.has(nk)) continue;
    const label = (list[0].mandal || "").trim() || "Unspecified mandal";
    rows.push({
      key: label,
      label,
      total: list.length,
      byStatus: countStatuses(list),
    });
  }

  return rows.sort((a, b) => a.label.localeCompare(b.label, "en"));
}

/**
 * Villages under the mandal: IGRS village list when available, merged with applicant data.
 */
export function villageReportRows(
  apps: Application[],
  district: string,
  mandal: string,
  geo: GeoDirectoryStore,
): GeoReportRow[] {
  const mandalNorm = normalizeGeoLabel(mandal);
  const sub = apps.filter(
    (a) =>
      storedDistrictMatchesDirectoryKey(geo, district, a.district) &&
      normalizeGeoLabel(a.mandal || "") === mandalNorm,
  );
  const appMap = new Map<string, Application[]>();
  for (const a of sub) {
    const raw = (a.village || "").trim();
    const nk = raw ? normalizeGeoLabel(raw) : "";
    if (!appMap.has(nk)) appMap.set(nk, []);
    appMap.get(nk)!.push(a);
  }

  const dirVillages = geo.tgVillagesForMandal(district, mandal);
  const rows: GeoReportRow[] = [];
  const usedNorm = new Set<string>();

  for (const v of dirVillages) {
    const nk = normalizeGeoLabel(v.name);
    const list = appMap.get(nk) ?? [];
    usedNorm.add(nk);
    rows.push({
      key: v.name,
      label: v.name,
      total: list.length,
      byStatus: countStatuses(list),
    });
  }

  for (const [nk, list] of appMap) {
    if (!nk || usedNorm.has(nk)) continue;
    const label = (list[0].village || "").trim() || "Unspecified village";
    rows.push({
      key: label,
      label,
      total: list.length,
      byStatus: countStatuses(list),
    });
  }

  return rows.sort((a, b) => a.label.localeCompare(b.label, "en"));
}

export function applicationsInVillage(
  apps: Application[],
  district: string,
  mandal: string,
  village: string,
  geo?: GeoDirectoryStore | null,
): Application[] {
  const mn = normalizeGeoLabel(mandal);
  const vn = normalizeGeoLabel(village);
  return apps.filter((a) => {
    if (geo && !storedDistrictMatchesDirectoryKey(geo, district, a.district)) return false;
    if (!geo && normalizeGeoLabel(a.district || "") !== normalizeGeoLabel(district)) return false;
    return normalizeGeoLabel(a.mandal || "") === mn && normalizeGeoLabel(a.village || "") === vn;
  });
}

/** Table / CSV columns (maps underlying statuses to portfolio language). */
export type GeoReportDisplay = {
  totalReceived: number;
  verificationPending: number;
  onHold: number;
  /** Ready for ICICI + awaiting subcontractor (HO / ICICI compliance track). */
  compliance: number;
  subcontracted: number;
};

export function rowToReportDisplay(row: GeoReportRow): GeoReportDisplay {
  return {
    totalReceived: row.total,
    verificationPending: row.byStatus.pending_validation,
    onHold: row.byStatus.on_hold,
    compliance:
      row.byStatus.ready_for_icici + row.byStatus.ready_for_mou + row.byStatus.icici_recorded,
    subcontracted: row.byStatus.subcontractor_linked,
  };
}

export const GEO_REPORT_COLUMN_LABELS = [
  "Total received",
  "Verification pending",
  "On hold",
  "Compliance",
  "Subcontracted",
] as const;

export function geoReportCsvHeaders(areaColumnLabel: string): string[] {
  return [areaColumnLabel, ...GEO_REPORT_COLUMN_LABELS];
}

export function geoReportRowToCsvCells(row: GeoReportRow): string[] {
  const d = rowToReportDisplay(row);
  return [
    row.label,
    String(d.totalReceived),
    String(d.verificationPending),
    String(d.onHold),
    String(d.compliance),
    String(d.subcontracted),
  ];
}
